import React from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import QrCode from 'qrcode'

import { useTheme } from '@mui/material/styles'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { useContextHook } from '../store/context'
import { toReadableTimestamp } from '../utils/dateUtils'
import InfoElement from '../components/fields/infoElement'
import { generateAbsoluteUrl, get, patch, setPageTitle } from '../utils/service'
import Alert from '../components/alert/alert'
import { getEvaluators } from '../utils/cuppingUtils'
import { CuppingSession } from '../types/cuppingSession'
import { CuppingSessionStatus, CuppingSessionStatusString } from '../types/enums/cuppingSessionStatus'
import ConfirmationDialog from '../components/dialog/confirmationDialog'
import { setCuppingRoastIds } from '../store/actions'
import Results from '../components/cupping/results'
import { LEFT_COLUMN_WIDTH } from '../utils/theme'

const CuppingDetailView = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const theme = useTheme()

  const { state, dispatch } = useContextHook()
  const [qrCode, setQrCode] = React.useState<string>('')
  const [cuppingSession, setCuppingSession] = React.useState<CuppingSession | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [updatingStatus, setUpdatingStatus] = React.useState<CuppingSessionStatus | null>(null)
  const [statusUpdateError, setStatusUpdateError] = React.useState<CuppingSessionStatus | null>(null)
  const [isDeleteSessionModalOpen, setIsDeleteSessionModalOpen] = React.useState<boolean>(false)
  const [isCopiedToClipboard, setIsCopiedToClipboard] = React.useState<boolean>(false)
  const [isCloseSessionWarningOpen, setIsCloseSessionWarningOpen] = React.useState<boolean>(false)
  const [isMergeDraftSessionWarningOpen, setIsMergeDraftSessionWarningOpen] = React.useState<boolean>(false)
  const [isQrCodeFullscreen, setIsQrCodeFullscreen] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (params.cuppingId) {
      setIsLoading(true)
      get<CuppingSession>(`cuppingsessions/${params.cuppingId}/`)
        .then((result) => {
          setIsLoading(false)
          setCuppingSession(result.data)
          setPageTitle(result.data.name)
        })
        .catch(() => {
          setIsLoading(false)
          setHasLoadError(true)
        })
    }
  }, [params.cuppingId])

  const patchCuppingStatus = (newStatus: CuppingSessionStatus): void => {
    setUpdatingStatus(newStatus)
    setStatusUpdateError(null)
    patch<{ status: CuppingSessionStatus }, CuppingSession>(cuppingSession?.url || '', { status: newStatus })
      .then((response) => {
        setUpdatingStatus(null)
        setCuppingSession(response.data)
      })
      .catch(() => {
        setStatusUpdateError(newStatus)
        setUpdatingStatus(null)
      })
  }

  const copySession = (merge?: boolean): void => {
    if (cuppingSession) {
      const selectedRoasts = cuppingSession.cupping_samples.map((x) => x.log || x.external_log_name || '')
      if (merge) {
        // remove duplicates on merge
        ;(state.cuppingRoastIds || []).forEach((roastId: string) => {
          if (!selectedRoasts.find((selectedRoastId: string) => roastId === selectedRoastId)) {
            selectedRoasts.push(roastId)
          }
        })
      }
      dispatch(setCuppingRoastIds(selectedRoasts))
      navigate(`/cuppings/create`, {
        state: { cuppingData: cuppingSession },
      })
    }
  }

  const getScoresUrl = (isAbsolute?: boolean): string => {
    const url = `/cuppings/addscore/${cuppingSession?.id}/`
    return isAbsolute ? generateAbsoluteUrl(url) : url
  }

  const generateQrCodeElement = (): JSX.Element => {
    return (
      <Box
        component="img"
        sx={{
          height: '98%',
          width: '100%',
          objectFit: 'contain',
          cursor: isQrCodeFullscreen ? 'zoom-out' : 'zoom-in',
          // light background frame for easier scanning of QR code on Android devices
          backgroundColor: theme.palette.qrcode.light,
          padding: 1,
        }}
        alt={t('cupping.qrcodealttext')}
        src={qrCode}
        onClick={() => setIsQrCodeFullscreen((oldValue) => !oldValue)}
      />
    )
  }

  if (!qrCode && cuppingSession?.id) {
    QrCode.toDataURL(
      getScoresUrl(true),
      { margin: 0, scale: 15, color: { light: theme.palette.qrcode.light, dark: theme.palette.qrcode.dark } },
      (_err: Error, url: string) => {
        setQrCode(url)
      }
    )
  }

  return (
    <Container
      component="main"
      maxWidth={false}
      sx={isQrCodeFullscreen ? { display: 'flex', flex: 1, overflow: 'hidden' } : undefined}
    >
      <Dialog open={isMergeDraftSessionWarningOpen} onClose={(): void => setIsMergeDraftSessionWarningOpen(false)}>
        <DialogTitle>{t('cupping.copysession')}</DialogTitle>
        <DialogContent>
          {t('cupping.copysessionbody').replace('{0}', `${(state.cuppingRoastIds || []).length}`)}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button onClick={() => setIsMergeDraftSessionWarningOpen(false)}>Close</Button>
          <Button variant="outlined" onClick={() => copySession()}>
            {t('cupping.copywithoutadding')}
          </Button>
          <Button variant="contained" onClick={() => copySession(true)}>
            {t('cupping.copyandadd')}
          </Button>
        </DialogActions>
      </Dialog>
      {isCloseSessionWarningOpen && cuppingSession && (
        <ConfirmationDialog
          header={t('cupping.endcuppingsession')}
          body={`${t('cupping.endsessionbody')} ${cuppingSession.is_blind ? t('cupping.endsessionblind') : ''}`}
          confirmButtonText={t('cupping.endsession')}
          onConfirm={(): void => {
            patchCuppingStatus(CuppingSessionStatus.Done)
            setIsCloseSessionWarningOpen(false)
          }}
          onClose={(): void => setIsCloseSessionWarningOpen(false)}
        />
      )}
      {isDeleteSessionModalOpen && cuppingSession && (
        <ConfirmationDialog
          header={t('cupping.deletecuppingsession')}
          body={t('cupping.deletecuppingsessionbody')}
          confirmButtonText={t('cupping.deletesession')}
          type="error"
          onConfirm={(): void => {
            patchCuppingStatus(CuppingSessionStatus.Deleted)
            setIsDeleteSessionModalOpen(false)
          }}
          onClose={(): void => setIsDeleteSessionModalOpen(false)}
        />
      )}
      {isLoading && <CircularProgress />}
      {hasLoadError && <Alert severity="error">{t('cupping.loadsessionerror')}</Alert>}
      {qrCode && isQrCodeFullscreen && generateQrCodeElement()}
      {cuppingSession && !isQrCodeFullscreen && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
          <Stack spacing={2} width={{ xs: '100%', sm: LEFT_COLUMN_WIDTH }}>
            <InfoElement label={t('cupping.cuppingsessionname')} value={cuppingSession.name} />
            <InfoElement label={t('cupping.date')} value={toReadableTimestamp(cuppingSession.created)} />
            <InfoElement label={t('cupping.cuppingform')} value={cuppingSession.cupping_form_data.name} />
            <InfoElement label={t('cupping.status')} value={CuppingSessionStatusString[cuppingSession.status]} />
            <InfoElement label={t('cupping.numberofsamples')} value={cuppingSession.cupping_samples.length} />
            <InfoElement
              label={t('cupping.blindcupping')}
              value={cuppingSession.is_blind ? t('cupping.blindcuppingyes') : t('cupping.blindcuppingno')}
            />
            <InfoElement label={t('cupping.evaluators')} value={getEvaluators(cuppingSession).join(', ') || '-'} />
            {cuppingSession.status === CuppingSessionStatus.InProgress && qrCode && (
              <Stack>
                {generateQrCodeElement()}
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(getScoresUrl(true))
                    setIsCopiedToClipboard(true)
                  }}
                >
                  {isCopiedToClipboard ? t('cupping.copiedtoclipboard') : t('cupping.copylinktoclipboard')}
                </Button>
              </Stack>
            )}
            {statusUpdateError && <Alert severity="error">{t('cupping.updatestatuserror')}</Alert>}
            <Stack direction="column" spacing={1}>
              {cuppingSession.status === CuppingSessionStatus.InProgress && (
                <LoadingButton
                  onClick={() => setIsCloseSessionWarningOpen(true)}
                  disabled={!!updatingStatus}
                  loading={updatingStatus === CuppingSessionStatus.Done}
                >
                  {t('cupping.endsession')}
                </LoadingButton>
              )}
              {cuppingSession.status === CuppingSessionStatus.InProgress && (
                <Button variant="contained" disabled={!!updatingStatus} component={Link} to={getScoresUrl()}>
                  {t('cupping.joinsession')}
                </Button>
              )}
              {cuppingSession.status === CuppingSessionStatus.Done && (
                <Button
                  disabled={!!updatingStatus}
                  onClick={() => {
                    if ((state.cuppingRoastIds || []).length > 0) {
                      setIsMergeDraftSessionWarningOpen(true)
                    } else {
                      copySession()
                    }
                  }}
                >
                  {t('cupping.copysession')}
                </Button>
              )}
              {cuppingSession.status === CuppingSessionStatus.Done && (
                <LoadingButton
                  variant="outlined"
                  onClick={() => patchCuppingStatus(CuppingSessionStatus.InProgress)}
                  disabled={!!updatingStatus}
                  loading={updatingStatus === CuppingSessionStatus.InProgress}
                >
                  {t('cupping.reopensession')}
                </LoadingButton>
              )}
              {cuppingSession.status === CuppingSessionStatus.Done && (
                <LoadingButton
                  variant="outlined"
                  color="error"
                  disabled={!!updatingStatus}
                  loading={updatingStatus === CuppingSessionStatus.Deleted}
                  onClick={() => setIsDeleteSessionModalOpen(true)}
                >
                  {t('cupping.deletesession')}
                </LoadingButton>
              )}
            </Stack>
          </Stack>
          <Results session={cuppingSession} showAverageSlider={true} />
        </Stack>
      )}
    </Container>
  )
}

export default CuppingDetailView
